import { AR, AT, BR, CA, Country, DE, GB, IE, NL, PL, US } from './country';
import { Dutch, English, French, German, Language, Polish, Portuguese, Spanish } from './language';

export class Locale {
  public static readonly 'en-US' = new Locale(US, English);
  public static readonly 'en-CA' = new Locale(CA, English);
  public static readonly 'en-GB' = new Locale(GB, English);
  public static readonly 'en-IE' = new Locale(IE, English);
  public static readonly 'en-DE' = new Locale(DE, English, false);
  public static readonly 'en-NL' = new Locale(NL, English);

  // this is necessary to allow CS agents to troubleshoot in english for LA prod
  public static readonly 'en-AR' = new Locale(AR, English);

  public static readonly 'de-DE' = new Locale(DE, German);
  public static readonly 'de-AT' = new Locale(AT, German);
  public static readonly 'es-AR' = new Locale(AR, Spanish);
  public static readonly 'es-US' = new Locale(US, Spanish, false);
  public static readonly 'fr-CA' = new Locale(CA, French);
  public static readonly 'pt-BR' = new Locale(BR, Portuguese, false);
  public static readonly 'pl-PL' = new Locale(PL, Polish, false);
  public static readonly 'nl-NL' = new Locale(NL, Dutch);

  public static readonly ALL = [
    Locale['en-US'], Locale['en-CA'], Locale['en-DE'], Locale['en-GB'], Locale['en-IE'], Locale['en-AR'],
    Locale['de-DE'], Locale['de-AT'],
    Locale['es-AR'], Locale['es-US'],
    Locale['fr-CA'],
    Locale['pt-BR'],
    Locale['pl-PL'],
    Locale['en-NL'], Locale['nl-NL'],
  ]
    .filter(locale => locale.enabled);

  public static isLanguageTag(str: string): str is LanguageTag {
    return /^[a-z]{2}(?:-[A-Z]{2})?$/.test(str);
  }

  public static isLanguageCode(str: string): str is LanguageCode {
    const strToLowerCase = str.toLowerCase();

    return /^[a-z]{2}$/.test(strToLowerCase) && Language.ALL.some(l => l.code === strToLowerCase);
  }

  public static getDefaultLocale(language: LanguageCode): FullLanguageTag {
    const languageObj = Language.ALL.find(lang => lang.code === language.toLowerCase());
    const localeToExtractFrom = Locale.ALL.find(locale => locale.language === languageObj) || Locale['en-US'];

    return localeToExtractFrom.languageTag;
  }

/**
 * normalizeLocale function converts locale strings with underscores to use the dash
 * Also, converts language codes to a full locale (default locale for that language).
 * Example: 'en' => 'en-US'
 *          'en_US' => 'en-US'
 *          'en-us' => 'en-US'
 */
  public static normalizeLanguageTag(languageTag: string): FullLanguageTag {
    if (Locale.isFullLanguageTag(languageTag)) return languageTag;

    if (Locale.isLanguageCode(languageTag)) {
      return Locale.getDefaultLocale(languageTag);
    } else {
      const [languageCode, countryCode] = languageTag.split(/[-_]/);

      if (!countryCode) return 'en-US';

      const normalizedLanguageTag = `${languageCode.toLowerCase()}-${countryCode.toUpperCase()}`;

      if (Locale.isFullLanguageTag(normalizedLanguageTag)) return normalizedLanguageTag;
    }

    return 'en-US';
  }

  public static isFullLanguageTag(str: string): str is FullLanguageTag {
    // check if the format is of xx-YY
    if (/^[a-z]{2}-[A-Z]{2}$/.test(str)) {
      const [languageCode, countryCode] = str.split(/[-_]/);

      // safety check for verifying that languageCode and countryCode are VALID
      return Language.ALL.some(l => l.code === languageCode) && Country.ALL.some(c => c.code === countryCode);
    }

    return false;
  }

  private constructor(
    public readonly country: Country,
    public readonly language: Language,
    public readonly enabled = true,
    public readonly languageTag = `${language.code}-${country.code.toUpperCase()}` as FullLanguageTag
  ) {}

  public readonly toString = () : string => `${this.language.name} (${this.country.displayName})`;
}

export function parseLocale(locale = ''): { lang: LanguageCode; country: CountryCode } {
  const parsedLocale = locale.split(/_|-/);

  return { lang: parsedLocale[0] as LanguageCode, country: parsedLocale[1] as CountryCode };
}
